import type { IPublicClientApplication } from '@azure/msal-browser';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

// Keyed by instance (via WeakMap) then by sorted scope string, so concurrent requests
// for different scope sets each get their own popup and the correct token.
const interactiveTokenPromises = new WeakMap<IPublicClientApplication, Map<string, Promise<string | undefined>>>();

const compareAlphabetically = (left: string, right: string): number =>
  left.localeCompare(right, undefined, { sensitivity: 'base' });

export const requestAccessToken = async (
  instance: IPublicClientApplication,
  scopes: string[],
): Promise<string | undefined> => {
  if (scopes.length === 0) return undefined;

  const account = instance.getActiveAccount() ?? instance.getAllAccounts().at(0);
  if (account == null) {
    console.warn('[MSAL] Unable to get access token: no account found.');
    return undefined;
  }

  try {
    await instance.initialize();
    const token = await instance.acquireTokenSilent({
      account,
      scopes,
    });

    return token.accessToken;
  } catch (error) {
    if (!(error instanceof InteractionRequiredAuthError)) {
      console.error('[MSAL] Silent token acquisition failed.', error);
      return undefined;
    }

    // Build a deterministic key so ['scope2','scope1'] and ['scope1','scope2'] share a slot.
    const scopeKey = [...scopes].sort(compareAlphabetically).join(' ');

    let instancePromises = interactiveTokenPromises.get(instance);
    if (!instancePromises) {
      instancePromises = new Map();
      interactiveTokenPromises.set(instance, instancePromises);
    }

    if (!instancePromises.has(scopeKey)) {
      const promise = instance
        .acquireTokenPopup({
          account,
          scopes,
        })
        .then((result) => result.accessToken)
        .catch((popupError: unknown) => {
          console.error('[MSAL] Interactive token acquisition failed.', popupError);
          return undefined;
        })
        .finally(() => {
          instancePromises.delete(scopeKey);
        });

      instancePromises.set(scopeKey, promise);
    }

    return instancePromises.get(scopeKey);
  }
};
